import React, { useCallback, useState, memo } from 'react';
import { useEmbedMode } from '../../../hooks/useEmbedMode';
import { Button, Form, Select } from 'antd';
import { DataSourceType } from '../hooks/useInitDataMap';
import { AddSystemModal, AddTableModal, UploadFileModal } from './modals';
import { SystemItem } from './items';
import { DataSourceTableType } from '@/types/dataMap';
import DataSourceDetailModal from './modals/DataSourceDetailModal';
import useDataMapPanel from '../hooks/useDataMapPanel';

interface DataMapPanelProps {
  onSystemChange?: (systems: DataSourceType[]) => void;
  systems: DataSourceType[];
  setSystems: (systems: DataSourceType[]) => void;
  onTableDragStart: (
    datasourceId: string,
    tableId: string,
    e: React.DragEvent
  ) => void;
  getDataSources: () => Promise<void>;
  mapViewsId: string | null;
}

// 筛选栏组件
const FilterBar: React.FC<{
  systems: DataSourceType[];
  filterSystemId: string | null;
  filterTableId: string | null;
  onFilterSystemChange: (value: string | null) => void;
  onFilterTableChange: (value: string | null) => void;
}> = memo(
  ({
    systems,
    filterSystemId,
    filterTableId,
    onFilterSystemChange,
    onFilterTableChange,
  }) => {
    return (
      <div className="mt-3 flex gap-2">
        <Select
          allowClear
          showSearch
          placeholder="数据源筛选"
          size="small"
          style={{ width: 140 }}
          value={filterSystemId ?? undefined}
          optionFilterProp="label"
          onChange={value => {
            const v = value ? String(value) : null;
            onFilterSystemChange(v);
            onFilterTableChange(null);
          }}
          options={systems.map(s => ({
            value: String(s.id),
            label: s.name || String(s.id),
          }))}
        />
        <Select
          allowClear
          showSearch
          placeholder="表筛选"
          size="small"
          style={{ width: 140 }}
          disabled={!filterSystemId}
          value={filterTableId ?? undefined}
          optionFilterProp="label"
          onChange={value => {
            onFilterTableChange(value ? String(value) : null);
          }}
          options={(() => {
            const current = systems.find(
              s => String(s.id) === String(filterSystemId)
            );
            return (current?.tables || []).map(t => ({
              value: String(t.id),
              label: t.tableName || String(t.id),
            }));
          })()}
        />
      </div>
    );
  }
);

FilterBar.displayName = 'FilterBar';

const DataMapPanel: React.FC<DataMapPanelProps> = memo(
  ({
    onSystemChange,
    systems,
    setSystems,
    onTableDragStart,
    getDataSources,
    mapViewsId,
  }) => {
    // 使用自定义钩子管理业务逻辑
    const {
      addSystemModalVisible,
      addTableModalVisible,
      uploadFileModalVisible,
      currentSystemId,
      selectedSystemId,
      selectedTableId,
      detailModalVisible,
      hasChanges,
      dataSourceTable,
      filterSystemId,
      filterTableId,
      filteredSystems,
      form,
      handleSystemClick,
      handleTableClick,
      handleFormValuesChange,
      handleSave,
      handleCancel,
      handleAddSystem,
      handleCloseAddSystemModal,
      handleSystemAdded,
      handleDeleteSystem,
      handleAddTable,
      handleCloseAddTableModal,
      handleTableAdded,
      handleDeleteTable,
      handleCloseUploadFileModal,
      handleFileUploaded,
      handleFilterSystemChange,
      handleFilterTableChange,
    } = useDataMapPanel({
      systems,
      setSystems,
      onSystemChange,
      onTableDragStart,
      getDataSources,
      mapViewsId,
    });

    const { isEmbedMode } = useEmbedMode();

    return (
      <>
        {/* 左侧数据源列表面板 */}
        <div
          className={`bg-white border border-[#e8edfb] py-4 flex flex-col w-80 ${isEmbedMode ? 'h-[calc(100vh-40px)]' : 'h-[calc(100vh-64px)]'}`}
        >
          <div className="mb-5 px-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[#132347] m-0 mr-3">
                数据源
              </h3>
              <Button type="primary" size="small" onClick={handleAddSystem}>
                + 添加系统
              </Button>
            </div>
            <FilterBar
              systems={systems}
              filterSystemId={filterSystemId}
              filterTableId={filterTableId}
              onFilterSystemChange={handleFilterSystemChange}
              onFilterTableChange={handleFilterTableChange}
            />
          </div>
          <div
            className="flex-1 overflow-y-auto"
            style={{ overflowY: 'auto', boxSizing: 'border-box', zIndex: 10 }}
          >
            {filteredSystems.map(system => (
              <SystemItem
                key={system.id}
                system={system}
                onDeleteSystem={handleDeleteSystem}
                onAddTable={handleAddTable}
                onTableDragStart={onTableDragStart}
                onSystemClick={handleSystemClick}
                onTableClick={handleTableClick}
                onDeleteTableClick={handleDeleteTable}
                isSelected={selectedSystemId === system.id}
              />
            ))}
          </div>
        </div>

        {/* 数据源详情模态框 */}
        <DataSourceDetailModal
          visible={detailModalVisible}
          onClose={() => handleCancel()}
          systems={systems}
          selectedSystemId={selectedSystemId}
          form={form}
          onValuesChange={handleFormValuesChange}
          onSave={handleSave}
          onCancel={handleCancel}
          hasChanges={hasChanges}
          onSystemClick={handleSystemClick}
          onDeleteSystem={handleDeleteSystem}
          onAddTable={handleAddTable}
          onTableDragStart={onTableDragStart}
          onTableClick={handleTableClick}
          handleAddSystem={handleAddSystem}
          onDeleteTableClick={handleDeleteTable}
        />

        {/* 添加系统模态框 */}
        <AddSystemModal
          visible={addSystemModalVisible}
          onClose={handleCloseAddSystemModal}
          onSystemAdded={handleSystemAdded}
          // currentSystemId={currentSystemId}
        />

        {/* 添加表模态框 */}
        <AddTableModal
          visible={addTableModalVisible}
          onClose={handleCloseAddTableModal}
          onTableAdded={handleTableAdded}
          dataSourceTable={dataSourceTable}
        />

        {/* 上传文件模态框 */}
        <UploadFileModal
          visible={uploadFileModalVisible}
          onClose={handleCloseUploadFileModal}
          dataSourceId={currentSystemId || ''}
          dataSourceName={
            currentSystemId
              ? systems.find(s => String(s.id) === String(currentSystemId))
                  ?.name || ''
              : ''
          }
          onFileUploaded={handleFileUploaded}
        />
      </>
    );
  }
);

DataMapPanel.displayName = 'DataMapPanel';

export default DataMapPanel;
